import { z } from 'zod';
import { commaSeparated } from '../common/comma-separated';
import { IdsFilterQuerySchema } from '../common/ids-filter.schema';
import { CreateCompanyContactLinkSchema } from './company-contact-links.schemas';
import {
  CRM_COMPANY_STATUSES,
  CRM_COMPANY_TYPES,
  CrmCompanyStatusSchema,
  CrmCompanyTypeSchema,
  type CrmCompanyStatus,
  type CrmCompanyType,
} from './company-enums';
import { refersTo } from '../common/refers-to';

export {
  CRM_COMPANY_STATUSES,
  CRM_COMPANY_TYPES,
  CrmCompanyStatusSchema,
  CrmCompanyTypeSchema,
  type CrmCompanyStatus,
  type CrmCompanyType,
};

// The CRM Company (Firma) — a partner firm the workspace tracks (IMM-4). The
// contract TS symbols carry a `Crm` prefix because the shared api-contract
// barrel already exports `Company*` names for the organisation's OWN legal
// entity (`identity/company.schemas.ts`); the wire paths, DB table, permission
// strings, and event `type` strings all stay the plain `company` — they are
// namespaced by module (`relationship:company` vs `identity:company`) and do
// not collide.
//
export const CRM_COMPANY_NAME_MAX_LENGTH = 120;
export const CRM_COMPANY_NOTES_MAX_LENGTH = 5_000;
export const CRM_COMPANY_TAG_MAX_LENGTH = 200;

export const CrmCompanyAddressSchema = z.object({
  street: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string(),
});
export type CrmCompanyAddress = z.infer<typeof CrmCompanyAddressSchema>;

const CrmCompanyAddressBodySchema = z.object({
  street: z.string().trim().min(1).max(120).nullable().default(null),
  houseNumber: z.string().trim().min(1).max(10).nullable().default(null),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/)
    .nullable()
    .default(null),
  city: z.string().trim().min(1).max(80).nullable().default(null),
  country: z.string().trim().min(1).max(80).default('Deutschland'),
});

// Every writable company field. `name` is the only required one; the rest are
// optional at creation (business-card capture). Reused verbatim for update so a
// company edit replaces the whole profile in one call.
const CrmCompanyWritableSchema = z.object({
  name: z.string().trim().min(1).max(CRM_COMPANY_NAME_MAX_LENGTH),
  type: CrmCompanyTypeSchema.nullable().default(null),
  legalForm: z.string().trim().min(1).max(40).nullable().default(null),
  registerNumber: z.string().trim().min(1).max(40).nullable().default(null),
  vatId: z.string().trim().min(1).max(20).nullable().default(null),
  taxNumber: z.string().trim().min(1).max(20).nullable().default(null),
  address: CrmCompanyAddressBodySchema.nullable().default(null),
  phone: z.string().trim().min(1).max(32).nullable().default(null),
  email: z.email().nullable().default(null),
  website: z.url().max(2_048).nullable().default(null),
  notes: z.string().trim().min(1).max(CRM_COMPANY_NOTES_MAX_LENGTH).nullable().default(null),
  tags: z.array(z.string().trim().min(1).max(CRM_COMPANY_TAG_MAX_LENGTH)).default([]),
});

// The optional `contactLink` block enables the atomic "capture a firm with its
// first contact" flow (IMM-4.3): when present, the company AND the contact-company
// link are created in one transaction. Absent → only the company is created.
export const CreateCrmCompanyBodySchema = CrmCompanyWritableSchema.extend({
  contactLink: CreateCompanyContactLinkSchema.optional(),
});
export type CreateCrmCompanyBody = z.infer<typeof CreateCrmCompanyBodySchema>;

export const CreateCrmCompanyPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const UpdateCrmCompanyBodySchema = CrmCompanyWritableSchema;
export type UpdateCrmCompanyBody = z.infer<typeof UpdateCrmCompanyBodySchema>;

export const UpdateCrmCompanyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const CrmCompanyResponseSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  name: z.string(),
  type: CrmCompanyTypeSchema.nullable(),
  legalForm: z.string().nullable(),
  registerNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  taxNumber: z.string().nullable(),
  address: CrmCompanyAddressSchema.nullable(),
  phone: z.string().nullable(),
  email: z.email().nullable(),
  website: z.string().nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  status: CrmCompanyStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  // Optimistic-lock token (IMM-1046.1) — system-assigned, never accepted from
  // a caller. Bumped on every write of the row; never on create.
  version: z.number().int().positive(),
});
export type CrmCompanyResponse = z.infer<typeof CrmCompanyResponseSchema>;

export const CreateCrmCompanyResponseSchema = CrmCompanyResponseSchema;
export type CreateCrmCompanyResponse = z.infer<typeof CreateCrmCompanyResponseSchema>;

// Slim list-row projection. `primaryPhone` is the company's single phone;
// `contactCount` (Anzahl Kontakte) is `0` until the ContactCompanyRole join
// lands in IMM-4.3.
export const CrmCompanyListItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  type: CrmCompanyTypeSchema.nullable(),
  city: z.string().nullable(),
  primaryPhone: z.string().nullable(),
  contactCount: z.number().int(),
});
export type CrmCompanyListItem = z.infer<typeof CrmCompanyListItemSchema>;

// Optional free-text search applied as a single combined match across name,
// city, and e-mail. Absent or blank (`?q=`) → no search.
export const CrmCompanySearchTermSchema = z
  .string()
  .optional()
  .transform((value) => {
    const trimmed = value?.trim();

    return trimmed === undefined || trimmed === '' ? undefined : trimmed;
  });

// Workspace-addressed visible-Standort list (mirrors the contact list, minus
// the assigned-to-self arm — companies carry no assignee). `type` filters by
// category; `sort` defaults to Name ascending per the PRD.
export const ListCrmCompaniesPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

export const ListCrmCompaniesQuerySchema = IdsFilterQuerySchema.extend({
  q: CrmCompanySearchTermSchema,
  type: CrmCompanyTypeSchema.optional(),
  organizationIds: commaSeparated(z.uuid()),
  sort: z.enum(['name', 'city', 'updated']).default('name'),
  direction: z.enum(['asc', 'desc']).default('asc'),
  // "Archivierte anzeigen" toggle — the default list shows only ACTIVE
  // companies; `?includeArchived=true` widens it to include ARCHIVED ones.
  includeArchived: z.stringbool().optional(),
});
export type ListCrmCompaniesQuery = z.infer<typeof ListCrmCompaniesQuerySchema>;

export const ListCrmCompaniesResponseSchema = z.object({
  data: z.array(CrmCompanyListItemSchema),
});
export type ListCrmCompaniesResponse = z.infer<typeof ListCrmCompaniesResponseSchema>;

export const GetWorkspaceCrmCompanyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});
export const GetWorkspaceCrmCompanyResponseSchema = CrmCompanyResponseSchema;
export type GetWorkspaceCrmCompanyResponse = z.infer<typeof GetWorkspaceCrmCompanyResponseSchema>;

export const GetVisibleCrmCompanyPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
export const GetVisibleCrmCompanyResponseSchema = CrmCompanyResponseSchema;
export type GetVisibleCrmCompanyResponse = z.infer<typeof GetVisibleCrmCompanyResponseSchema>;

export const UpdateCrmCompanyResponseSchema = CrmCompanyResponseSchema;
export type UpdateCrmCompanyResponse = z.infer<typeof UpdateCrmCompanyResponseSchema>;

// Lifecycle transitions (IMM-4.2). Archive / un-archive return the updated
// company; soft-delete returns the deleted id.
export const CrmCompanyLifecyclePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const ArchiveCrmCompanyResponseSchema = CrmCompanyResponseSchema;
export type ArchiveCrmCompanyResponse = z.infer<typeof ArchiveCrmCompanyResponseSchema>;

export const UnarchiveCrmCompanyResponseSchema = CrmCompanyResponseSchema;
export type UnarchiveCrmCompanyResponse = z.infer<typeof UnarchiveCrmCompanyResponseSchema>;

export const DeleteCrmCompanyResponseSchema = z.object({ id: z.uuid() });
export type DeleteCrmCompanyResponse = z.infer<typeof DeleteCrmCompanyResponseSchema>;
